import { useState, useCallback } from 'react';

/**
 * Custom Hook für Topic Tag Management
 * 
 * Verwaltet:
 * - Liste von Topic Tags
 * - Aktuelles Tag-Input
 * - Tag hinzufügen/entfernen
 */
export function useTopicTags(initialTags = []) {
  const [topicTags, setTopicTags] = useState(initialTags);
  const [currentTag, setCurrentTag] = useState('');
  
  /**
   * Add current tag to list (if valid and not duplicate)
   */
  const addTag = useCallback(() => {
    const trimmed = currentTag.trim();
    if (trimmed && !topicTags.includes(trimmed)) {
      setTopicTags(prev => [...prev, trimmed]);
      setCurrentTag('');
      return true;
    }
    return false;
  }, [currentTag, topicTags]);
  
  /**
   * Add tag on Enter key press
   */
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  }, [addTag]);
  
  /**
   * Remove a specific tag
   */
  const removeTag = useCallback((tag) => {
    setTopicTags(prev => prev.filter(t => t !== tag));
  }, []);
  
  /**
   * Clear all tags
   */
  const clearTags = useCallback(() => {
    setTopicTags([]);
    setCurrentTag('');
  }, []);
  
  /**
   * Set multiple tags at once
   */
  const setTags = useCallback((tags) => {
    setTopicTags(Array.isArray(tags) ? tags : []);
  }, []);
  
  /**
   * Add multiple tags at once (filters duplicates)
   */
  const addTags = useCallback((newTags) => {
    if (!Array.isArray(newTags)) return;
    
    setTopicTags(prev => {
      const combined = [...prev];
      newTags.forEach(tag => {
        const trimmed = tag.trim();
        if (trimmed && !combined.includes(trimmed)) {
          combined.push(trimmed);
        }
      });
      return combined;
    });
  }, []);
  
  return {
    // State
    topicTags,
    currentTag,
    
    // Setters
    setTopicTags,
    setCurrentTag,
    setTags,
    
    // Actions
    addTag,
    addTags,
    removeTag,
    clearTags,
    handleKeyPress,
    
    // Utilities
    hasTag: (tag) => topicTags.includes(tag),
    tagCount: topicTags.length,
    isEmpty: topicTags.length === 0
  };
}

export default useTopicTags;
